// platform-ui/src/lib/hooks/useLocalizedValidator.ts
'use client';

import { useMemo } from 'react';
import { useLocale } from 'next-intl';
import { customizeValidator } from '@rjsf/validator-ajv8';
import type { CustomValidatorOptionsType, Localizer } from '@rjsf/validator-ajv8';
import Ajv2020 from 'ajv/dist/2020';
import enLocalizer from 'ajv-i18n/localize/en';
import svLocalizer from 'ajv-i18n/localize/sv';
import type { Locale } from '@/i18n/config';

/**
 * Map of supported locales to their ajv-i18n localizer functions.
 * Add new locale imports here as needed.
 */
const localizerMap: Record<Locale, Localizer> = {
  en: enLocalizer,
  sv: svLocalizer,
};

/**
 * Shared validator options matching rjsfValidator.ts configuration.
 */
const validatorOptions: CustomValidatorOptionsType = {
  ajvOptionsOverrides: {
    // Don't try to fetch/validate meta-schemas
    validateSchema: false,
    // Be lenient with unknown formats
    strict: false,
    // Collect all errors, not just first
    allErrors: true,
  },
  // Use Ajv2020 to support JSON Schema 2020-12 features (unevaluatedProperties)
  AjvClass: Ajv2020 as never,
};

/**
 * useLocalizedValidator - Creates a locale-aware RJSF validator
 *
 * Returns a memoized RJSF validator configured with ajv-i18n localization.
 * The validator is recreated when locale changes, ensuring validation error
 * messages are displayed in the user's language.
 *
 * @returns RJSF-compatible validator with localized error messages
 *
 * @example
 * ```tsx
 * const validator = useLocalizedValidator();
 *
 * <Form
 *   schema={schema}
 *   validator={validator}
 *   formData={data}
 * />
 * ```
 */
export function useLocalizedValidator() {
  const locale = useLocale() as Locale;

  const validator = useMemo(() => {
    const localizer = localizerMap[locale] ?? localizerMap.en;

    return customizeValidator(validatorOptions, localizer);
  }, [locale]);

  return validator;
}
